import { languagesMap } from '@app/translations/i18n/i18n.service';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

// Import delle localizzazioni di moment.js
import 'moment/locale/de';
import 'moment/locale/en-gb';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/hu';
import 'moment/locale/it';
import 'moment/locale/pl';
import 'moment/locale/pt';
import 'moment/locale/ro';
import 'moment/locale/sl';

export const DEFAULT_DATE_FORMAT = 'yyyy-MM-DD';

// Mappa delle lingue supportate per moment.js (basata su languagesMap da i18n.service)
const MOMENT_LOCALES = Object.fromEntries(
  Object.entries(languagesMap).map(([key, value]) => [value, key])
);

// Formati di data localizzati comuni
export const LOCALIZED_DATE_FORMATS = {
  SHORT: 'L', // 01/12/2024 (formato locale)
  MEDIUM: 'll', // 1 dic 2024 (formato locale)
  LONG: 'LL', // 1 dicembre 2024 (formato locale)
  FULL: 'LLLL', // lunedì 1 dicembre 2024 15:30 (formato locale)
  TIME: 'LT', // 15:30 (formato locale)
  DATETIME: 'L LT', // 01/12/2024 15:30 (formato locale)
  ISO: 'YYYY-MM-DD', // 2024-12-01 (ISO)
  DISPLAY: 'DD/MM/YYYY', // 01/12/2024 (display fisso)
  DISPLAY_WITH_TIME: 'DD/MM/YYYY HH:mm', // 01/12/2024 15:30 (display fisso)
  COMPACT: 'DD MMMM YYYY', // 01 Dicembre 2024 (formato compatto localizzato)
};

/**
 * Formatta una data usando moment.js con localizzazione automatica
 * @param date - La data da formattare (stringa, Date, o moment)
 * @param format - Il formato da utilizzare (default: DISPLAY)
 * @param translateService - Il servizio di traduzione per ottenere la lingua corrente
 * @returns La data formattata nella lingua corrente
 */
export const formatLocalizedDate = (
  date: string | Date | moment.Moment,
  format: string = LOCALIZED_DATE_FORMATS.DISPLAY,
  translateService?: TranslateService
): string => {
  const momentDate = moment(date);

  if (translateService) {
    const currentLang = translateService.currentLang || 'it_IT';
    const momentLocale = MOMENT_LOCALES[currentLang] || 'it';

    // Imposta la locale di moment.js
    momentDate.locale(momentLocale);
  }

  return momentDate.format(format);
};

/**
 * Formatta una data per il display con localizzazione automatica
 * @param date - La data da formattare
 * @param translateService - Il servizio di traduzione
 * @returns La data formattata per il display (DD/MM/YYYY o formato locale)
 */
export const formatDisplayDate = (
  date: string | Date | moment.Moment,
  translateService?: TranslateService
): string => {
  return formatLocalizedDate(
    date,
    LOCALIZED_DATE_FORMATS.DISPLAY,
    translateService
  );
};

/**
 * Formatta una data con ora per il display con localizzazione automatica
 * @param date - La data da formattare
 * @param translateService - Il servizio di traduzione
 * @returns La data e ora formattata per il display
 */
export const formatDisplayDateTime = (
  date: string | Date | moment.Moment,
  translateService?: TranslateService
): string => {
  return formatLocalizedDate(
    date,
    LOCALIZED_DATE_FORMATS.DISPLAY_WITH_TIME,
    translateService
  );
};

/**
 * Formatta una data per le traduzioni con il translateService
 * @param date - La data da formattare
 * @param translateService - Il servizio di traduzione
 * @param format - Il formato da utilizzare (default: DISPLAY)
 * @returns La data formattata nella lingua corrente
 */
export const formatDateForTranslation = (
  date: string | Date | moment.Moment,
  translateService: TranslateService,
  format: string = LOCALIZED_DATE_FORMATS.DISPLAY
): string => {
  return formatLocalizedDate(date, format, translateService);
};

/**
 * Formatta una data relativa (es. "2 giorni fa", "ieri", "oggi")
 * @param date - La data da formattare
 * @param translateService - Il servizio di traduzione
 * @returns La data relativa formattata nella lingua corrente
 */
export const formatRelativeDate = (
  date: string | Date | moment.Moment,
  translateService?: TranslateService
): string => {
  const momentDate = moment(date);

  if (translateService) {
    const currentLang = translateService.currentLang || 'it_IT';
    const momentLocale = MOMENT_LOCALES[currentLang] || 'it';
    momentDate.locale(momentLocale);
  }

  return momentDate.fromNow();
};

/**
 * Funzione legacy per compatibilità - formatta una data senza localizzazione
 * @param date - La data da formattare
 * @param format - Il formato da utilizzare (default: DEFAULT_DATE_FORMAT)
 * @returns La data formattata
 */
export const formatDate = (
  date: string | any,
  format: string = DEFAULT_DATE_FORMAT
) => {
  return moment(date).format(format);
};
